/**
 * JobCompliancePanel — Required Compliance widget for a job detail page.
 *
 * Surfaces every compliance doc linked to the job and (for the operative
 * viewing the page) lets them tap "I've read this" to record an acknowledgement
 * timestamp. Admins see an audit summary of who has acked the current version.
 *
 * Props:
 *   - jobId:        id of the job
 *   - mode:         "ack"   — render acknowledge buttons (operative view)
 *                   "audit" — read-only audit list (admin view)
 *                   "auto"  — pick based on user role (default)
 *   - onAckChange:  optional callback fired after a successful ack so the
 *                   parent can re-render the gating UI (e.g. "Mark Arrived").
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import API from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle, Warning, DownloadSimple, ArrowSquareOut, ClockCounterClockwise, Plus, Trash,
} from "@phosphor-icons/react";
import { downloadAuthFile } from "../lib/fileOpen";
import ComplianceDocsPicker from "./ComplianceDocsPicker";

const ADMIN_ROLES = ["super_admin", "admin", "project_manager"];

// Category labels — kept in sync with the backend categories list. We keep a
// local map so the panel can render without a second round-trip just for labels.
const CATEGORY_LABEL = {
  rams: "RAMS",
  risk_assessment: "Risk Assessment",
  method_statement: "Method Statement",
  coshh: "COSHH",
  safety_data_sheet: "Safety Data Sheet",
  health_and_safety: "Health & Safety",
  company_documents: "Company Documents",
};

export default function JobCompliancePanel({ jobId, mode = "auto", onAckChange, onStatusChange }) {
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const effectiveMode = mode === "auto" ? (isAdmin ? "audit" : "ack") : mode;

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acking, setAcking] = useState({}); // {docId: bool}
  // Admin-only "Attach Compliance" dialog state
  const [attachOpen, setAttachOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/jobs/${jobId}/compliance-hub`);
      setDocs(data?.documents || []);
    } catch (e) {
      // 403 on this endpoint just means the user has no read access — silently
      // hide the panel, don't toast.
      if (e.response?.status !== 403) {
        toast.error("Could not load compliance: " + (e.response?.data?.detail || e.message));
      }
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { if (jobId) load(); }, [jobId, load]);

  const ackedCount = useMemo(
    () => docs.filter(d => !!d.current_user_ack).length,
    [docs],
  );
  const total = docs.length;
  const allAcked = total > 0 && ackedCount === total;

  // Publish ack readiness up to the parent whenever it changes — used to gate
  // "Mark Arrived" / "Complete job" buttons until the operative has read every
  // linked RAMS / Method Statement.
  useEffect(() => {
    if (!loading && onStatusChange) {
      onStatusChange({ total, ackedCount, allAcked, loaded: true });
    }
  }, [loading, total, ackedCount, allAcked, onStatusChange]);

  const onAck = async (doc) => {
    if (!doc.current_file_id) {
      toast.error("This document has no uploaded PDF yet — ask an admin to upload it.");
      return;
    }
    setAcking(s => ({ ...s, [doc.id]: true }));
    try {
      await API.post(`/jobs/${jobId}/compliance-hub/${doc.id}/acknowledge`);
      toast.success(`Acknowledged: ${doc.title}`);
      await load();
      onAckChange && onAckChange();
    } catch (e) {
      toast.error("Could not record ack: " + (e.response?.data?.detail || e.message));
    } finally {
      setAcking(s => ({ ...s, [doc.id]: false }));
    }
  };

  // Hide the whole widget when there's nothing to surface — avoids visual noise
  // on every single job. Admins still see an empty-state hint though, because
  // the absence might be the bug they're investigating.
  const onUnlink = async (doc) => {
    if (!window.confirm(
      `Unlink '${doc.title}' from this job?\n\nThe document stays in the Compliance Hub. ` +
      `Any acknowledgements already given against it are preserved as audit history.`
    )) return;
    try {
      await API.post(`/jobs/${jobId}/compliance-hub/unlink`, { doc_ids: [doc.id] });
      toast.success(`Unlinked '${doc.title}'`);
      load();
    } catch (e) {
      toast.error("Unlink failed: " + (e.response?.data?.detail || e.message));
    }
  };
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-4" data-testid="job-compliance-panel-loading">
        <p className="text-xs text-muted-foreground">Loading required compliance…</p>
      </div>
    );
  }
  if (total === 0) {
    if (effectiveMode === "audit") {
      return (
        <>
          <div className="bg-card border border-border rounded-lg p-4" data-testid="job-compliance-panel">
            <Header
              total={0}
              ackedCount={0}
              mode="audit"
              isAdmin={isAdmin}
              onAttach={() => setAttachOpen(true)}
            />
            <p className="text-xs text-muted-foreground mt-2">
              No compliance documents linked to this job.
              {isAdmin ? (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={() => setAttachOpen(true)}
                    className="text-primary hover:underline"
                    data-testid="job-compliance-attach-empty-link"
                  >
                    Attach one from the library →
                  </button>
                </>
              ) : (
                <Link to="/compliance-hub" className="text-primary hover:underline ml-1">
                  Link some in the Compliance Hub →
                </Link>
              )}
            </p>
          </div>
          {isAdmin && (
            <AttachComplianceDialog
              key={`attach-${attachOpen}`}
              open={attachOpen}
              onOpenChange={setAttachOpen}
              jobId={jobId}
              alreadyLinkedIds={[]}
              onAttached={() => { setAttachOpen(false); load(); }}
            />
          )}
        </>
      );
    }
    return null;
  }

  return (
    <TooltipProvider>
      <div
        className={`bg-card border rounded-lg p-4 ${effectiveMode === "ack" && !allAcked ? "border-amber-300 dark:border-amber-700" : "border-border"}`}
        data-testid="job-compliance-panel"
      >
        <Header
          total={total}
          ackedCount={ackedCount}
          mode={effectiveMode}
          isAdmin={isAdmin && effectiveMode === "audit"}
          onAttach={() => setAttachOpen(true)}
        />
        <div className="space-y-2 mt-3">
          {docs.map(d => (
            <DocRow
              key={d.id}
              doc={d}
              mode={effectiveMode}
              busy={!!acking[d.id]}
              isAdmin={isAdmin}
              onAck={() => onAck(d)}
              onUnlink={() => onUnlink(d)}
            />
          ))}
        </div>
      </div>
      {isAdmin && (
        <AttachComplianceDialog
          key={`attach-${attachOpen}`}
          open={attachOpen}
          onOpenChange={setAttachOpen}
          jobId={jobId}
          alreadyLinkedIds={docs.map(d => d.id)}
          onAttached={() => { setAttachOpen(false); load(); }}
        />
      )}
    </TooltipProvider>
  );
}

function Header({ total, ackedCount, mode, isAdmin, onAttach }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        <ShieldCheck size={18} weight="duotone" className="text-primary" />
        <p className="font-semibold text-sm">Required Compliance</p>
      </div>
      <div className="flex items-center gap-2">
        {total > 0 && (
          <Badge
            variant={ackedCount === total ? "default" : "outline"}
            className={ackedCount === total ? "bg-emerald-600 hover:bg-emerald-600" : ""}
            data-testid="job-compliance-progress"
          >
            {mode === "ack" ? `${ackedCount} of ${total} acknowledged` : `${total} document${total === 1 ? "" : "s"}`}
          </Badge>
        )}
        {isAdmin && onAttach && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onAttach}
            data-testid="job-compliance-attach-btn"
            className="h-7 text-xs"
          >
            <Plus size={12} className="mr-1" /> Attach
          </Button>
        )}
      </div>
    </div>
  );
}

function DocRow({ doc, mode, busy, isAdmin, onAck, onUnlink }) {
  const ack = doc.current_user_ack;
  const noPdf = !doc.current_file_id;
  const showUnlink = isAdmin && mode === "audit" && onUnlink;
  return (
    <div
      className="flex items-start gap-3 border border-border rounded-md p-2.5 bg-background"
      data-testid={`job-compliance-doc-${doc.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{CATEGORY_LABEL[doc.category] || doc.category}</Badge>
          {doc.current_version_number > 0 && (
            <Badge variant="secondary" className="text-[9px]">v{doc.current_version_number}</Badge>
          )}
          {(doc.tags || []).slice(0, 2).map(t => (
            <span key={t} className="text-[9px] text-muted-foreground">#{t}</span>
          ))}
        </div>
        <Link
          to={`/compliance-hub/${doc.id}`}
          className="font-medium text-sm hover:underline truncate block"
          title={doc.title}
        >
          {doc.title}
        </Link>
        {ack && mode === "ack" && (
          <p className="text-[10px] text-emerald-700 dark:text-emerald-400 flex items-center gap-1 mt-0.5">
            <CheckCircle size={11} weight="fill" />
            Acknowledged {new Date(ack.acknowledged_at).toLocaleString()}
          </p>
        )}
        {mode === "audit" && (
          <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
            <ClockCounterClockwise size={11} />
            {(doc.all_acks_count || 0) === 0
              ? "No one has acknowledged this version yet"
              : `Acknowledged by ${doc.all_acks_count} ${doc.all_acks_count === 1 ? "person" : "people"}`}
          </p>
        )}
        {noPdf && (
          <p className="text-[10px] text-amber-700 dark:text-amber-400 flex items-center gap-1 mt-0.5">
            <Warning size={11} weight="fill" />
            No PDF uploaded yet
          </p>
        )}
      </div>

      <div className="flex items-center gap-1 shrink-0">
        {doc.current_file_id && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => downloadAuthFile(`/api/files/${doc.current_file_id}/download`, doc.current_file_name)}
                data-testid={`job-compliance-download-${doc.id}`}
              >
                <DownloadSimple size={14} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Download PDF</TooltipContent>
          </Tooltip>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Link to={`/compliance-hub/${doc.id}`}>
              <Button variant="ghost" size="sm" data-testid={`job-compliance-open-${doc.id}`}>
                <ArrowSquareOut size={14} />
              </Button>
            </Link>
          </TooltipTrigger>
          <TooltipContent>Open document</TooltipContent>
        </Tooltip>

        {showUnlink && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                onClick={onUnlink}
                className="text-muted-foreground hover:text-red-600"
                data-testid={`job-compliance-unlink-${doc.id}`}
              >
                <Trash size={14} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Unlink from this job</TooltipContent>
          </Tooltip>
        )}

        {mode === "ack" && (
          ack ? (
            <Button
              size="sm"
              variant="ghost"
              disabled
              className="text-emerald-700 dark:text-emerald-400 disabled:opacity-100"
              data-testid={`job-compliance-acked-${doc.id}`}
            >
              <CheckCircle size={14} className="mr-1" weight="fill" /> Acknowledged
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={onAck}
              disabled={busy || noPdf}
              data-testid={`job-compliance-ack-${doc.id}`}
            >
              {busy ? "Saving…" : "I've read this"}
            </Button>
          )
        )}
      </div>
    </div>
  );
}

/**
 * AttachComplianceDialog — admin-only dialog for linking compliance docs
 * to an EXISTING job. Mirrors the picker shown inside the Job Create
 * dialog but pre-filters out docs already attached so the admin can't
 * "re-pick" the same one. POSTs the selected ids to the bulk-link endpoint
 * and refreshes the parent panel on success.
 */
function AttachComplianceDialog({ jobId, alreadyLinkedIds, open, onOpenChange, onAttached }) {
  const [allDocs, setAllDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoadingDocs(true);
    (async () => {
      try {
        const { data } = await API.get("/compliance-hub/documents");
        if (!cancelled) setAllDocs(data?.documents || []);
      } catch (e) {
        if (!cancelled) toast.error("Could not load documents: " + (e.response?.data?.detail || e.message));
      } finally {
        if (!cancelled) setLoadingDocs(false);
      }
    })();
    return () => { cancelled = true; };
    // Parent remounts via key, so this only fires once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const availableDocs = useMemo(
    () => allDocs.filter(d => !alreadyLinkedIds.includes(d.id)),
    [allDocs, alreadyLinkedIds],
  );

  const onSubmit = async () => {
    if (!selectedIds.length) {
      toast.error("Pick at least one document");
      return;
    }
    setBusy(true);
    try {
      const { data } = await API.post(`/jobs/${jobId}/compliance-hub/link`, { doc_ids: selectedIds });
      const n = data?.linked || selectedIds.length;
      toast.success(`Linked ${n} document${n === 1 ? "" : "s"} to this job`);
      onAttached && onAttached();
    } catch (e) {
      toast.error("Link failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="job-compliance-attach-dialog">
        <DialogHeader>
          <DialogTitle>Attach compliance documents</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Pick the compliance documents this job requires. The assigned operative will be HARD-BLOCKED
            from starting / completing the job until every selected doc has been acknowledged.
          </p>
          {loadingDocs ? (
            <p className="text-xs text-muted-foreground italic">Loading documents…</p>
          ) : availableDocs.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              {allDocs.length === 0
                ? <>The Compliance Hub is empty. <Link to="/compliance-hub" className="text-primary hover:underline">Upload your first PDF →</Link></>
                : "Every document in the hub is already linked to this job."}
            </p>
          ) : (
            <ComplianceDocsPicker
              docs={availableDocs}
              selectedIds={selectedIds}
              onChange={setSelectedIds}
            />
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
          <Button
            onClick={onSubmit}
            disabled={busy || !selectedIds.length}
            data-testid="job-compliance-attach-submit"
          >
            {busy ? "Linking…" : `Attach ${selectedIds.length || ""}`.trim()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
